import type React from "react";
import { Link } from "react-router-dom";
import type {
  TalentFilters,
  TalentListItem,
} from "../../../hooks/use-talents-api";
import { useCrmPermission } from "../../../hooks/useCrmPermission";
import type { RestApiError } from "../../../lib/rest-client";
import {
  getAffiliationLabel,
  getEmploymentTypeLabel,
  getWorkStatusBadge,
} from "../utils";

type TalentListPageProps = {
  talents: TalentListItem[];
  total: number;
  loading: boolean;
  error: RestApiError | null;
  filters: TalentFilters;
  onSearchChange: (search: string) => void;
  onAffiliationChange: (affiliation: string) => void;
  onEmploymentTypeChange: (employmentType: string) => void;
  onPageChange: (page: number) => void;
  itemsPerPage: number;
};

/**
 * タレント一覧ページのプレゼンテーションコンポーネント
 * @param {object} props プロパティ
 * @param {Array} props.talents タレント一覧
 * @param {number} props.total 総件数
 * @param {boolean} props.loading ローディング状態
 * @param {object} props.error エラー
 * @param {object} props.filters フィルター状態
 * @param {Function} props.onSearchChange 検索変更ハンドラ
 * @param {Function} props.onAffiliationChange 所属変更ハンドラ
 * @param {Function} props.onEmploymentTypeChange 雇用形態変更ハンドラ
 * @param {Function} props.onPageChange ページ変更ハンドラ
 * @param {number} props.itemsPerPage 1ページあたりの件数
 * @return {React.ReactElement} タレント一覧ページ
 */
export const TalentListPage: React.FC<TalentListPageProps> = ({
  talents,
  total,
  loading,
  error,
  filters,
  onSearchChange,
  onAffiliationChange,
  onEmploymentTypeChange,
  onPageChange,
  itemsPerPage,
}) => {
  const { canEdit } = useCrmPermission();
  const currentPage = filters.page || 1;
  const totalPages = Math.ceil(total / itemsPerPage);

  return (
    <div className="p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold">タレント管理</h1>
        {canEdit && (
          <Link to="/crm/talents/new" className="btn btn-primary btn-sm">
            新規登録
          </Link>
        )}
      </div>

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <input
          type="text"
          placeholder="名前・スキルで検索..."
          className="input input-bordered input-sm w-64"
          value={filters.search || ""}
          onChange={e => onSearchChange(e.target.value)}
        />
        <select
          className="select select-bordered select-sm"
          value={filters.affiliation || ""}
          onChange={e => onAffiliationChange(e.target.value)}
        >
          <option value="">全て</option>
          <option value="proprietary">自社</option>
          <option value="partner">パートナー</option>
        </select>
        <select
          className="select select-bordered select-sm"
          value={filters.employmentType || ""}
          onChange={e => onEmploymentTypeChange(e.target.value)}
        >
          <option value="">全て</option>
          <option value="full_time">正社員</option>
          <option value="freelancer">フリーランス</option>
          <option value="contract">契約社員</option>
        </select>
      </div>

      {/* Error */}
      {error && (
        <div className="alert alert-error mb-4">
          <span>データの取得に失敗しました: {error.message}</span>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex justify-center py-8">
          <span className="loading loading-spinner loading-lg" />
        </div>
      )}

      {/* Table */}
      {!loading && (
        <>
          <div className="card bg-base-100 shadow-md overflow-hidden">
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>名前</th>
                    <th>所属</th>
                    <th>雇用形態</th>
                    <th>スキル</th>
                    <th>稼働状態</th>
                    <th>希望単価</th>
                    <th>担当</th>
                    <th>操作</th>
                  </tr>
                </thead>
                <tbody>
                  {talents.map(talent => {
                    const status = getWorkStatusBadge(talent.workStatus ?? "");
                    return (
                      <tr key={talent.id} className="hover">
                        <td>
                          <Link
                            to={`/crm/talents/${talent.id}`}
                            className="link link-primary font-medium"
                          >
                            {talent.name}
                          </Link>
                        </td>
                        <td>{getAffiliationLabel(talent.affiliation ?? "")}</td>
                        <td>
                          {getEmploymentTypeLabel(talent.employmentType ?? "")}
                        </td>
                        <td>
                          <div className="flex flex-wrap gap-1">
                            {talent.skills.slice(0, 3).map(skill => (
                              <span
                                key={skill}
                                className="badge badge-outline badge-sm"
                              >
                                {skill}
                              </span>
                            ))}
                            {talent.skills.length > 3 && (
                              <span className="badge badge-ghost badge-sm">
                                +{talent.skills.length - 3}
                              </span>
                            )}
                          </div>
                        </td>
                        <td>
                          <span
                            className={`badge badge-sm ${status.className}`}
                          >
                            {status.label}
                          </span>
                        </td>
                        <td>
                          {talent.desiredReward != null
                            ? `${talent.desiredReward.toLocaleString()}円`
                            : "-"}
                        </td>
                        <td>{talent.assigneeName || "-"}</td>
                        <td>
                          <Link
                            to={`/crm/talents/${talent.id}`}
                            className="btn btn-ghost btn-xs"
                          >
                            詳細
                          </Link>
                        </td>
                      </tr>
                    );
                  })}
                  {talents.length === 0 && (
                    <tr>
                      <td
                        colSpan={8}
                        className="text-center py-8 text-base-content/60"
                      >
                        タレントが見つかりません
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-center mt-6">
              <div className="join">
                <button
                  type="button"
                  className="join-item btn btn-sm"
                  disabled={currentPage <= 1}
                  onClick={() => onPageChange(currentPage - 1)}
                >
                  &laquo;
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                  page => (
                    <button
                      type="button"
                      key={page}
                      className={`join-item btn btn-sm ${
                        page === currentPage ? "btn-active" : ""
                      }`}
                      onClick={() => onPageChange(page)}
                    >
                      {page}
                    </button>
                  ),
                )}
                <button
                  type="button"
                  className="join-item btn btn-sm"
                  disabled={currentPage >= totalPages}
                  onClick={() => onPageChange(currentPage + 1)}
                >
                  &raquo;
                </button>
              </div>
            </div>
          )}

          <div className="text-sm text-base-content/60 mt-2 text-center">
            {total}件中 {(currentPage - 1) * itemsPerPage + 1}-
            {Math.min(currentPage * itemsPerPage, total)}件を表示
          </div>
        </>
      )}
    </div>
  );
};
